import type { AutoFixLoopToggleError } from '@/data/auto-fix-loop/use-auto-fix-loop-toggle';
import type { AutoFixAllowanceView, AutoFixPause } from '@/lib/auto-fix-allowance/model';
import type { AutoFixLoopStatusView } from '@/lib/auto-fix-loop/status';
import type { AutoFixLoopWork } from '@/lib/auto-fix-loop/types';

import { resolveAutoFixLoopViewActivity } from '@/lib/auto-fix-loop/presentation';

/** What the auto-fix line under cubic says, one kind at a time. */
export type AutoFixLine =
  /** cubic is reviewing; auto-fix acts on the result. */
  | { kind: 'queued' }
  /** A run is working on `work`: review findings, or the failing CI checks of the head. */
  | { kind: 'fixing'; work: AutoFixLoopWork; runUrl: string | null }
  /** Switched off while a run still works. */
  | { kind: 'finishing'; runUrl: string | null }
  /** The loop finished and left cubic threads (`count`) and/or failing CI checks for a person. */
  | { kind: 'for-you'; count: number; failingCheckCount: number }
  /** The loop finished clean; `count` is every finding and check its runs fixed, never 0. */
  | { kind: 'fixed'; count: number; runUrl: string | null }
  | { kind: 'failed'; runUrl: string | null }
  /** The Auto Fix PR allowance or the PR's run limit holds the loop; `pause` says why. */
  | {
      kind: 'paused';
      pause: AutoFixPause;
      /** The organization's live usage: room in it offers Resume before the reset. */
      allowance: AutoFixAllowanceView | null;
      installationId: number | null;
      /** Whether the viewer can change the plan; see `AutoFixLoopStatusView`. */
      canManageBilling: boolean | null;
    };

/**
 * The line, or `loading` while it hangs on a fact that has not arrived. A ready
 * `null` line has nothing to say: off, on and idle, done with nothing fixed, or
 * a stale failure while enabling.
 */
export type AutoFixLineResolution =
  | { kind: 'loading' }
  | { kind: 'ready'; line: AutoFixLine | null };

/**
 * Whether cubic is reviewing the pull request, as its reviewer row shows it;
 * `loading` until its runs load.
 */
export type CubicReviewActivity = 'loading' | 'running' | 'idle';

export function toCubicReviewActivity(runs: {
  isLoading: boolean;
  running: boolean;
}): CubicReviewActivity {
  if (runs.isLoading) {
    return 'loading';
  }
  return runs.running ? 'running' : 'idle';
}

const LOADING: AutoFixLineResolution = { kind: 'loading' };

function ready(line: AutoFixLine | null): AutoFixLineResolution {
  return { kind: 'ready', line };
}

/**
 * The line for a loop's status. Which of a run, a review in progress, a
 * failure, or a finish wins is `resolveAutoFixLoopActivity`'s, the one the
 * inbox tooltip shows.
 */
export function resolveAutoFixLine(params: {
  data: AutoFixLoopStatusView;
  pendingTarget: boolean | null;
  cubicReview: CubicReviewActivity;
  /** cubic threads still open on the pull request; `undefined` until known. */
  openIssueCount: number | undefined;
}): AutoFixLineResolution {
  const { data, pendingTarget, cubicReview, openIssueCount } = params;
  const latest = data.iterations[0];
  const running = latest?.status === 'running';
  const runUrl = latest?.sessionUrl ?? null;
  if (!data.enabled) {
    // A disabled switch explains itself in its tooltip. Closing a pull request
    // cancels its run, and each run posted its session link on the pull request.
    return ready(
      running && data.eligibility?.reason !== 'pr_not_open' ? { kind: 'finishing', runUrl } : null,
    );
  }
  // Short of a run or a pause, the line hangs on whether cubic is reviewing: guessing "not
  // reviewing" would show a failure or a finish, then swap it for "queued". A pause reads the
  // same whatever cubic is doing. An enable in flight reads `off` and a stale `error` as
  // waiting (`resolveAutoFixLoopViewActivity`), which neither runs nor pauses.
  if (data.status !== 'running' && data.status !== 'paused' && cubicReview === 'loading') {
    return LOADING;
  }
  const activity = resolveAutoFixLoopViewActivity({
    data,
    pendingTarget,
    cubicReviewRunning: cubicReview === 'running',
    openIssueCount,
  });
  switch (activity.kind) {
    case 'fixing':
      return ready({ kind: 'fixing', work: activity.work, runUrl });
    case 'paused':
      // A paused view always carries its pause; without one there is nothing to explain.
      return ready(
        data.pause && {
          kind: 'paused',
          pause: data.pause,
          allowance: data.allowance,
          installationId: data.installationId,
          canManageBilling: data.canManageBilling ?? null,
        },
      );
    case 'queued':
      return ready({ kind: 'queued' });
    case 'waiting':
      return ready(null);
    case 'failed':
      return ready({ kind: 'failed', runUrl });
    case 'threads-left':
      return ready({
        kind: 'for-you',
        count: activity.openIssueCount,
        failingCheckCount: activity.failingCheckCount,
      });
    case 'done': {
      // cubic's open threads tell a clean finish from one that left threads for a person.
      if (openIssueCount === undefined) {
        return LOADING;
      }
      // The same count as the Slack finish: each finding and check once, across every run.
      return ready(data.fixedCount > 0 ? { kind: 'fixed', count: data.fixedCount, runUrl } : null);
    }
    default:
      return activity satisfies never;
  }
}

/** A refused flip, in the words shown under the line. */
export function describeAutoFixToggleRefusal(
  error: Pick<AutoFixLoopToggleError, 'message' | 'code' | 'target'>,
): string {
  if (error.code !== 'no_write_access') {
    return error.message.replace(/\.$/, '');
  }
  const action = error.target ? 'enable' : 'disable';
  return `Only the pull request author or someone with write access can ${action} auto-fix`;
}
